// Standalone plan checkout button, so the marketing partner can drop a single
// "Get Pro — $17.99/mo" button anywhere in their layout without embedding the
// whole pricing table.
//
// This is also how a pricing page reaches Stripe in one click. A plain link to
// our site cannot: the visitor's token lives in localStorage partitioned under
// (partner's site + our origin), so a top-level navigation to us lands in a
// different bucket where they are signed out. Only a frame from our origin ON
// their page can see that session — this component.
//
// URL contract (see planEmbedConfig in utils/embedOptions.js):
//   /?embed=plan&plan=starter               → monthly Starter
//   /?embed=plan&plan=pro&cycle=yearly      → yearly Pro
//   /?embed=plan&plan=team&label=Buy%20now  → custom button label
//   ...&price=0                             → hide the price, label only
//   ...&fill=1                              → stretch to the iframe, for
//                                             full-width pricing-card CTAs
//
// Clicking, by case:
// - Signed in without an active subscription: Stripe Checkout opens in a popup
//   over the host page (opened during this click so popup blockers allow it);
//   blocked popup falls back to the full-page redirect inside handleCheckout.
// - Signed out: hand off to the converter frame if one shares the page — it has
//   room for the sign-in dialog. Otherwise a popup signs them in and carries on
//   to Stripe in that same window, so the partner's page is never left.
// - Already subscribed: the plan-change confirmation needs a dialog, so hand off
//   to the converter frame; standing alone, send them to the billing portal,
//   which needs no room here.
// - The free plan is a signup, not a purchase, and never reaches Stripe.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Box, CircularProgress } from '@mui/material';
import { useApp } from '../../context/AppContext';
import { planCards } from '../../data/plans';
import {
  requestFromConverterFrame, watchConverterPresence, REQUEST_CHECKOUT, REQUEST_AUTH,
} from '../../utils/embedChannel';
import { isSubscriptionActiveOrCanceling } from '../../utils/subscriptionHelpers';
import { openCheckoutPopup } from '../../utils/checkoutPopup';
import { openAuthPopup } from '../../utils/authPopup';
import { radii } from '../../theme/colors';

export default function PlanButtonEmbed({ planId, cycle, label, showPrice, fill }) {
  const {
    token, authOk, subscriptionStatus, handleCheckout, handleManageAccount,
    watchCheckoutPopup, setSnackbar,
  } = useApp();
  const [busy, setBusy] = useState(false);

  const plan = planCards.find(p => p.id === planId);
  const isYearly = cycle === 'yearly';
  const isFree = planId === 'free';
  // Matches PlanCard's id convention: yearly tiers carry a '_yearly' suffix.
  const checkoutPlanId = (isYearly && !isFree) ? `${planId}_yearly` : planId;

  // Whether a converter frame shares this page, learned at mount rather than at
  // click time: with no converter we open a popup, and window.open must run in
  // the same task as the click or the browser blocks it. Awaiting the 250 ms
  // handoff first would spend that user activation.
  const converterPresent = useRef(false);
  useEffect(() => watchConverterPresence(() => { converterPresent.current = true; }), []);

  const handleClick = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      // The free plan has no price to check out. Sending it anyway would be
      // charged as Starter — the backend's PRICE_CONFIG falls back to that for
      // any plan id it does not recognise — so this must never reach Stripe.
      if (isFree) {
        if (authOk || token) {
          setSnackbar({ open: true, message: 'You already have an account — the free plan is yours.', severity: 'info' });
          return;
        }
        if (converterPresent.current
          && await requestFromConverterFrame(REQUEST_AUTH, { tab: 'signup' })) return;
        if (openAuthPopup('signup')) return;
        window.open(`${window.location.origin}/?authPopup=1&tab=signup`, '_blank');
        return;
      }

      // Straight-to-Stripe case: no dialog stands between this click and
      // checkout, so keep the user activation and open the popup here. If
      // subscriptionStatus is stale and the API-side truth differs,
      // handleCheckout's own guards close the popup and hand over to a dialog.
      if (authOk && token && !isSubscriptionActiveOrCanceling(subscriptionStatus)) {
        const popup = openCheckoutPopup();
        await handleCheckout(checkoutPlanId, false, undefined, { popupWindow: popup });
        return;
      }

      // Signed out with no converter frame: sign in and buy in one popup. Doing
      // this before the handoff attempt is what keeps the click's user
      // activation — see the presence handshake above.
      if (!authOk && !token && !converterPresent.current) {
        const popup = openAuthPopup('signin', checkoutPlanId);
        if (popup) { watchCheckoutPopup(popup, checkoutPlanId); return; }
        // Popup blocked. Continue on the full site, which reads ?checkout= and
        // resumes after sign-in — a worse flow, but a working one.
        window.open(`${window.location.origin}/?checkout=${encodeURIComponent(checkoutPlanId)}`, '_blank', 'noopener');
        return;
      }

      // Signed out with a converter frame, or an existing subscriber changing
      // plan: both need a dialog, and the converter frame is the only one on the
      // page tall enough to show one.
      const handledElsewhere = await requestFromConverterFrame(REQUEST_CHECKOUT, { planId: checkoutPlanId });
      if (handledElsewhere) return;

      // An existing subscriber, standing alone. handleCheckout would open the
      // plan-change confirmation, which App.js does not mount in button-embed
      // mode — a dead button. The billing portal handles plan changes and needs
      // no room in this frame.
      if (token || authOk) { await handleManageAccount(); return; }

      // Signed out, the converter frame announced itself but never answered.
      const url = `${window.location.origin}/?checkout=${encodeURIComponent(checkoutPlanId)}`;
      window.open(url, '_blank', 'noopener');
    } finally {
      setBusy(false);
    }
  }, [
    busy, checkoutPlanId, isFree, token, authOk, subscriptionStatus,
    handleCheckout, handleManageAccount, watchCheckoutPopup, setSnackbar,
  ]);

  // Hooks run first, so this guard sits below them.
  if (!plan) {
    return <Box sx={{ font: '13px system-ui', color: '#8a1c24' }}>Unknown plan &ldquo;{planId}&rdquo;</Box>;
  }

  const price = isYearly ? (plan.yearlyPrice || plan.price) : plan.price;
  const period = isYearly ? (plan.yearlyPeriod || '/yr') : (plan.period || '');
  const text = label || plan.cta || `Get ${plan.name}`;
  const buttonLabel = showPrice ? `${text} — ${price}${period}` : text;
  const featured = Boolean(plan.badge);

  return (
    // Default: inline-flex with no padding keeps the iframe's usable area tight
    // to the button, so the partner can size the frame to the control itself.
    // With ?fill=1 the button takes the frame instead, so it can sit in a
    // pricing card whose other CTAs are full-width blocks.
    //
    // Fill pins to the viewport rather than using height: 100% — nothing between
    // here and <html> declares a height, so a percentage would resolve against
    // an auto-height ancestor and collapse back to the label.
    <Box sx={fill
      ? { position: 'fixed', inset: 0, display: 'flex', bgcolor: 'transparent' }
      : { display: 'inline-flex', bgcolor: 'transparent' }}
    >
      <Button
        variant={isFree ? 'outlined' : 'contained'}
        color={featured ? 'primary' : 'secondary'}
        onClick={handleClick}
        disabled={busy}
        sx={fill
          ? {
            width: '100%', height: '100%', borderRadius: `${radii.button}px`,
            fontSize: '0.875rem', fontWeight: 600, px: 1.5, py: 0, lineHeight: 1.25,
          }
          : { borderRadius: `${radii.button}px`, whiteSpace: 'nowrap' }}
        startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
      >
        {buttonLabel}
      </Button>
    </Box>
  );
}
